<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('warehouse.entry_sheet')"
    icon="mdi-package-down"
    color="primary"
    :save-text="$t('add')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    :show-lock="!!editedItem?.id"
    :locked="!!editedItem?.locked"
    @save="save"
    @lock="lockDocument"
  >
    <template v-slot:app-bar-actions>
      <warehouse-button-print-menu @print="print" />
    </template>

    <template v-if="editedItem?.id" v-slot:footer-actions>
      <v-btn
        color="primary"
        depressed
        large
        :disabled="!!editedItem?.locked"
        :dark="!!!editedItem?.locked"
        @click="update"
      >
        <v-icon left>mdi-content-save-edit</v-icon>
        {{ $t("update") }}
      </v-btn>
    </template>

    <v-form ref="form" v-model="valid" v-if="editedItem">
      <v-row>
        <v-col cols="12" sm="12" md="4" lg="4" xl="2">
          <choose-warehouse-field
            v-model="editedItem.warehouse"
            return-object
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <date-picker v-model="editedItem.document_date" single-date color="white" />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <autocomplete
            v-model="editedItem.ordered_by"
            :items="users"
            return-object
            :loading="loadingUsers"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.ordered_by')"
            required
            color="white"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <autocomplete
            v-model="editedItem.received_by"
            :items="users"
            return-object
            :loading="loadingUsers"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.received_by')"
            required
            color="white"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="4" lg="4" xl="2">
          <choose-supplier-field
            hide-details
            v-model="editedItem.supplier"
            color="white"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.invoice_number')"
            v-model="editedItem.document_number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <date-picker
            :label="$t('warehouse.shipping_date')"
            v-model="editedItem.document_date"
            single-date
            color="white"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" xl="2">
          <text-area
            color="white"
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-document-groceries-table
      :key="tableRenderKey"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      entry
      @removeGrocery="removeGrocery"
      v-if="selectedWarehouse"
    />

    <!-- <v-alert
      v-if="lastStocktakingDate"
      type="warning"
      dense
      text
      class="mt-3"
      icon="mdi-clipboard-alert-outline"
    >
      {{ $t(stocktakingHintKey).replace("{0}", lastStocktakingDate) }}
    </v-alert> -->
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasSelectedWarehouse from "@/mixins/warehouse/hasSelectedWarehouse";
import hasLastStocktaking from "@/mixins/warehouse/hasLastStocktaking";
import hasWarehouseUsers from "@/mixins/warehouse/hasWarehouseUsers";
import ChooseWarehouseField from "@/components/warehouse/fields/ChooseWarehouseField.vue";
import WarehouseDocumentGroceriesTable from "@/components/warehouse/shared/WarehouseDocumentGroceriesTable.vue";
import { waybillDocumentGrocery } from "@/models/warehouse";
import ChooseSupplierField from "@/components/warehouse/fields/ChooseSupplierField.vue";
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import WarehouseButtonPrintMenu from "@/components/warehouse/buttons/WarehouseButtonPrintMenu.vue";

export default {
  name: "WarehouseEntryGroceryDialog",
  mixins: [
    hasModel,
    hasPrintingV2,
    hasSnackbar,
    hasSelectedWarehouse,
    hasLastStocktaking,
    hasWarehouseUsers,
  ],
  components: {
    ChooseWarehouseField,
    WarehouseDocumentGroceriesTable,
    ChooseSupplierField,
    FullscreenDialog,
    WarehouseButtonPrintMenu,
  },
  props: {
    editedItem: {
      type: Object,
      default: { ...waybillDocumentGrocery },
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
    };
  },
  methods: {
    _buildParams(isEdit = false) {
      return {
        ...this.editedItem,
        document_type: "entry",
        transaction_type: "entry",
        supplier: this.editedItem?.supplier?.id || null,
        supplier_id: this.editedItem?.supplier?.id || null,
        ordered_by: this.editedItem?.ordered_by?.id || null,
        received_by: this.editedItem?.received_by?.id || null,
        groceries: (this.editedItem?.groceries || []).map((grocery) => {
          const netoQty = Number(grocery.amount) || 0;
          const unitPrice = Number(grocery.unit_price) || 0;
          const item = {
            grocery_id: grocery.id,
            warehouse_id: this.editedItem.warehouse.id,
            name: grocery.name,
            code: grocery.code,
            supplier_code: grocery.supplier_code,
            measure: grocery.measure,
            quantity: netoQty,
            gross_quantity: Number(grocery.gross_quantity) || 0,
            unit_price: unitPrice,
            amount: netoQty,
            transaction_type: "entry",
          };
          return { ...item, grocery: item };
        }),
        warehouse_id: this.editedItem.warehouse.id,
      };
    },
    async _submitDocument({ request, successMessage, errorPrefix }) {
      try {
        this.loading = true;
        await this.handleRequest(request, {
          successMessage,
          errorMessagePrefix: errorPrefix || this.$t("error"),
        });
        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Submit operation failed:", error);
      } finally {
        this.loading = false;
      }
    },
    async update() {
      const params = this._buildParams(true);
      const request = warehouseService.editDocumentTransaction(
        this.editedItem.id,
        params
      );
      await this._submitDocument({
        request,
        successMessage: this.$t("warehouse_update_success"),
      });
    },
    removeGrocery(grocery) {
      this.editedItem.groceries = this.editedItem.groceries.filter(
        (item) => grocery.id !== item.id
      );
    },
    print(withStock = false) {
      const {
        row,
        col,
        field,
        text,
        bold,
        group,
        table,
        line,
        spacer,
      } = this.printElements;

      const tableHeaders = [
        { text: this.$t("r_b"), value: "num", width: "60px" },
        { text: this.$t("title").toUpperCase(), value: "name" },
        {
          text: this.$t("unit_of_measurment"),
          printText: this.$t("unit_of_measurment").replace(" ", "<br/>"),
          value: "measure",
          width: "100px",
        },
        ...(withStock
          ? [
              {
                text: this.$t("warehouse.warehouse_status"),
                value: "warehouse_stock",
                align: "right",
                width: "120px",
              },
            ]
          : []),
        {
          text: this.$t("warehouse.quantity_gross"),
          value: "gross_quantity",
          align: "center",
          group: this.$t("quantity"),
          width: "120px",
        },
        {
          text: this.$t("warehouse.quantity_net"),
          value: "amount",
          align: "center",
          group: this.$t("quantity"),
          width: "120px",
        },
        {
          text: this.$t("price") + " " + this.restaurantCurrency,
          value: "unit_price",
          align: "right",
          width: "100px",
        },
        {
          text: this.$t("amount") + " " + this.restaurantCurrency,
          value: "total_amount",
          align: "right",
          width: "100px",
        },
      ];

      const items = this.editedItem.groceries.map((item, index) => {
        const netoQty = Number(item.amount) || 0;
        const unitPrice = Number(item.unit_price) || 0;
        return {
          ...item,
          num: index + 1,
          gross_quantity: item.gross_quantity != null ? item.gross_quantity : "",
          amount: netoQty,
          unit_price: unitPrice || "",
          total_amount: unitPrice ? (netoQty * unitPrice).toFixed(2) : "",
        };
      });

      const warehouseName = this.toLatin(this.editedItem.warehouse?.name || "");
      const supplierName = this.toLatin(this.editedItem.supplier?.name || "");
      const docDate = this.localeDate(this.editedItem.document_date);
      const docNumber = this.editedItem.number || "";
      const invoiceNumber = this.editedItem.document_number || "";

      const orderedByName = this.toLatin(
        [this.editedItem.ordered_by?.name, this.editedItem.ordered_by?.lastname]
          .filter(Boolean)
          .join(" ")
      );
      const receivedByName = this.toLatin(
        [this.editedItem.received_by?.name, this.editedItem.received_by?.lastname]
          .filter(Boolean)
          .join(" ")
      );
      this.printDoc(
        [
          // Row 1: org name | place + date
          row("between align-end", [
            field(warehouseName, this.$t("warehouse.org_name_and_place"), 300),
            row("gap-24", [
              field("", this.$t("warehouse.place"), 140),
              field(docDate, this.$t("date"), 120),
            ]),
          ]),
          // Row 2: entry goods no. | invoice number
          spacer(30),
          row("between align-end", [
            group(
              bold(this.$t("warehouse.entry_goods_no") + " ", 18),
              field(docNumber, "", 120)
            ),
            field(invoiceNumber, this.$t("warehouse.invoice_number"), 200),
          ]),
          // Row 3: received from
          spacer(30),
          row([
            group(text(this.$t("warehouse.received_from")), field(supplierName, "", 280)),
          ]),
          // Table
          spacer(20),
          table(tableHeaders, items),
          // Signature
          spacer(40),
          row("between", [
            col("center", [
              text(this.$t("warehouse.ordered_by")),
              spacer(16),
              line(),
              text(orderedByName),
            ]),
            col("center", [
              text(this.$t("warehouse.received_by")),
              spacer(16),
              line(),
              text(receivedByName),
            ]),
          ]),
        ],
        { pageTitle: this.$t("warehouse.entry_sheet") }
      );
    },
    async save() {
      const params = this._buildParams(false);
      const request = warehouseService.addDocumentTransaction(params);
      await this._submitDocument({
        request,
        successMessage: this.$t("warehouse_entry_success"),
      });
    },
    async lockDocument() {
      try {
        const response = await warehouseService.lockDocument(this.editedItem.id);
        this.editedItem.locked = response.data.locked;
      } catch (error) {
        console.error("Lock operation failed:", error);
      }
    },
  },
  computed: {
    tableRenderKey() {
      return [
        this.editedItem?.id || "new",
        this.selectedWarehouse?.id || "no-warehouse",
      ].join("-");
    },
    stocktakingHintKey() {
      return "warehouse.last_stocktaking_hint_entry";
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        {
          text: this.$t("code"),
          align: "start",
          value: "code",
          width: "150px",
        },
        { text: this.$t("title"), value: "name" },
        {
          text: "Šifra dobavljača",
          align: "start",
          value: "supplier_code",
          width: "150px",
        },
        // TODO: gross_quantity - needs backend model (currently front-end only, not saved to API)
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_gross")})`,
          value: "gross_quantity",
          align: "right",
        },
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_net")})`,
          value: "amount",
          align: "right",
        },
        {
          text: this.$t("warehouse.warehouse_status"),
          value: "warehouse_stock",
          print: false,
          align: "right",
          width: "160px",
        },
        { text: this.$t("unit_of_measurment"), value: "measure", width: "120px" },
        // TODO: unit_price - needs backend model (currently front-end only, not saved to API)
        { text: this.$t("price"), value: "unit_price", align: "right", width: "140px" },
        // ukupno = kolicina (neto) * cena
        {
          text: this.$t("amount"),
          value: "total_amount",
          align: "right",
          width: "140px",
          sortable: false,
        },
        { text: "", value: "actions", width: "80px" },
      ];
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.fetchUsers();
      }
    },
  },
};
</script>
